import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckIcon } from "lucide-react";
import { useState } from "react";

interface PricingPlan {
  monthly: {
    amount: number;
    currency: string;
    priceId: string;
  };
  yearly: {
    amount: number;
    currency: string;
    priceId: string;
  };
}

interface PricingData {
  region: string;
  currency: string;
  plans: {
    freelancer: PricingPlan;
    business: PricingPlan;
  };
}

export default function Pricing() {
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  
  // Use public plans API instead of pricing endpoint
  const { data: publicPlans, isLoading } = useQuery({
    queryKey: ['/api/public/plans'],
    queryFn: async () => {
      const response = await fetch('/api/public/plans?locale=sk');
      if (!response.ok) throw new Error('Failed to fetch plans');
      return response.json();
    },
  });

  // Transform API data to expected format
  const pricing = publicPlans?.data ? {
    region: 'SK',
    currency: 'EUR',
    plans: {
      freelancer: {
        monthly: {
          amount: publicPlans.data.find((p: any) => p.name === 'freelancer')?.prices?.find((pr: any) => pr.interval === 'month')?.amount || 9.90,
          currency: 'EUR',
          priceId: publicPlans.data.find((p: any) => p.name === 'freelancer')?.prices?.find((pr: any) => pr.interval === 'month')?.priceId || 'pri_01k2qszqf8m6ez5jyz60dzbamd'
        },
        yearly: {
          amount: publicPlans.data.find((p: any) => p.name === 'freelancer')?.prices?.find((pr: any) => pr.interval === 'year')?.amount || 99,
          currency: 'EUR',
          priceId: publicPlans.data.find((p: any) => p.name === 'freelancer')?.prices?.find((pr: any) => pr.interval === 'year')?.priceId || 'pri_01k2qt1835hmqjw5cj6p55he9e'
        }
      },
      business: {
        monthly: {
          amount: publicPlans.data.find((p: any) => p.name === 'business')?.prices?.find((pr: any) => pr.interval === 'month')?.amount || 19.90,
          currency: 'EUR', 
          priceId: publicPlans.data.find((p: any) => p.name === 'business')?.prices?.find((pr: any) => pr.interval === 'month')?.priceId || 'pri_01k2qt82ahzchqxjjhk0a0xax3'
        },
        yearly: {
          amount: publicPlans.data.find((p: any) => p.name === 'business')?.prices?.find((pr: any) => pr.interval === 'year')?.amount || 199,
          currency: 'EUR',
          priceId: publicPlans.data.find((p: any) => p.name === 'business')?.prices?.find((pr: any) => pr.interval === 'year')?.priceId || 'pri_01k2qt90avm947y935jgbgk9yd'
        }
      }
    }
  } : null;

  const handleCheckout = async (tier: 'freelancer' | 'business') => {
    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier,
          billing: billingInterval
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // TODO: Integrovať s Paddle Checkout
        console.log('Checkout data:', data);
        alert(`Checkout pripravený pre ${tier} - ${billingInterval}\nPrice ID: ${data.priceId}\nRegión: ${data.region}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Nastala chyba pri checkout procese');
    }
  };

  if (isLoading) {
    return (
      <div className="page-container py-6 sm:py-8">
        <div className="text-center">
          <h1 className="page-title text-2xl sm:text-3xl font-bold mb-8">Cenové plány</h1>
          <p>Načítavam ceny...</p>
        </div>
      </div>
    );
  }

  if (!pricing) {
    return (
      <div className="page-container py-6 sm:py-8">
        <div className="text-center">
          <h1 className="page-title text-2xl sm:text-3xl font-bold mb-8">Cenové plány</h1>
          <p>Chyba pri načítaní cien</p>
        </div>
      </div>
    );
  }

  const freelancerPlan = pricing.plans.freelancer;
  const businessPlan = pricing.plans.business;

  return (
    <div className="page-container py-6 sm:py-8">
      <div className="text-center mb-6 sm:mb-8 min-w-0">
        <h1 className="page-title text-2xl sm:text-3xl font-bold mb-4">Cenové plány</h1>
        <p className="text-muted-foreground mb-6">Vyberte si plán, ktorý vám vyhovuje</p>
        
        <div className="flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8">
          <Badge variant="outline">{pricing.region} región</Badge>
          <Badge variant="outline">{pricing.currency}</Badge>
        </div>
        
        {/* Billing Toggle */}
        <div className="inline-flex items-center bg-muted rounded-lg p-1 flex-wrap justify-center">
          <button
            onClick={() => setBillingInterval('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Mesačne
          </button>
          <button
            onClick={() => setBillingInterval('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              billingInterval === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Ročne
            <Badge variant="secondary" className="ml-2">-17%</Badge>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 w-full min-w-0">
        {/* Freelancer Plan */}
        <Card className="p-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2">Freelancer</h3>
            <p className="text-gray-600 mb-4">Pre jednotlivcov a malé projekty</p>
            <div className="mb-4">
              <span className="text-4xl font-bold">
                {freelancerPlan[billingInterval].amount}
              </span>
              <span className="text-xl text-gray-600 ml-1">
                {pricing.currency}
              </span>
              <span className="text-gray-500 text-sm">
                /{billingInterval === 'monthly' ? 'mesiac' : 'rok'}
              </span>
            </div>
            {billingInterval === 'yearly' && (
              <p className="text-sm text-green-600 font-medium">
                Úspora {Math.round((1 - (freelancerPlan.yearly.amount / 12) / freelancerPlan.monthly.amount) * 100)}% oproti mesačnému
              </p>
            )}
          </div>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>5 klientov</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Neobmedzené úlohy</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>50 faktúr/mesiac</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Email podpora</span>
            </li>
          </ul>
          
          <Button 
            className="w-full" 
            variant="outline"
            onClick={() => handleCheckout('freelancer')}
          >
            Vybrať Freelancer
          </Button>
        </Card>

        {/* Business Plan */}
        <Card className="p-6 border-primary">
          <div className="text-center mb-6">
            <Badge className="mb-2">Najpopulárnejší</Badge>
            <h3 className="text-2xl font-bold mb-2">Business</h3>
            <p className="text-gray-600 mb-4">Pre tímy a rastúce firmy</p>
            <div className="mb-4">
              <span className="text-4xl font-bold">
                {businessPlan[billingInterval].amount}
              </span>
              <span className="text-xl text-gray-600 ml-1">
                {pricing.currency}
              </span>
              <span className="text-gray-500 text-sm">
                /{billingInterval === 'monthly' ? 'mesiac' : 'rok'}
              </span>
            </div>
            {billingInterval === 'yearly' && (
              <p className="text-sm text-green-600 font-medium">
                Úspora {Math.round((1 - (businessPlan.yearly.amount / 12) / businessPlan.monthly.amount) * 100)}% oproti mesačnému
              </p>
            )}
          </div>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Neobmedzení klienti</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Neobmedzené úlohy</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Neobmedzené faktúry</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Tímová spolupráca</span>
            </li>
            <li className="flex items-start">
              <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Prioritná podpora</span>
            </li>
          </ul>
          
          <Button 
            className="w-full"
            onClick={() => handleCheckout('business')}
          >
            Vybrať Business
          </Button>
        </Card>
      </div>
      
      <div className="text-center mt-8 text-sm text-gray-500">
        <p>Price ID pre testovanie:</p>
        <p>Freelancer {billingInterval}: {freelancerPlan[billingInterval].priceId}</p>
        <p>Business {billingInterval}: {businessPlan[billingInterval].priceId}</p>
      </div>
    </div>
  );
}